/**
 * AdminHome — Role-aware command center landing page.
 * Route: /admin/home (separate from existing /admin which keeps its tabs).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Home, Bell, Search, Plus, Activity, Shield, Building2, CreditCard, Inbox, LifeBuoy,
  AlertTriangle, TrendingUp, DollarSign, Clock, ShieldAlert, FileWarning, ScrollText,
  CheckCircle2, XCircle, Circle, Users, LayoutDashboard, ArrowRight, ExternalLink,
  Megaphone, Sparkles, Radio, WifiOff, Church, QrCode, Database, Video, Film, Wifi,
} from 'lucide-react';
import { useAuthStore } from '@/lib/store';
import { getToken } from '@/lib/authToken';
import { CopilotBriefingWidget } from '@/components/copilot/CopilotBriefingWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const request = (method, path) => {
  const token = getToken();
  return axios({ method, url: `${API_URL}/api/admin/home${path}`, headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
};

const ICON_MAP = {
  'shield-alert': ShieldAlert, 'file-warning': FileWarning, 'dollar-sign': DollarSign,
  'clock': Clock, 'life-buoy': LifeBuoy, 'inbox': Inbox, 'alert-triangle': AlertTriangle,
  'scroll-text': ScrollText, 'credit-card': CreditCard,
};

const SEVERITY_STYLES = {
  critical: 'bg-rose-500/10 border-rose-400/40 text-rose-100',
  warning: 'bg-amber-500/10 border-amber-400/40 text-amber-100',
  info: 'bg-sky-500/10 border-sky-400/40 text-sky-100',
};

const AdminHome = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [liveStatus, setLiveStatus] = useState('connecting'); // connecting | live | offline
  const [liveFeed, setLiveFeed] = useState([]); // last 8 live alerts
  const wsRef = useRef(null);

  const { data: overview } = useQuery({
    queryKey: ['admin-home-overview'],
    queryFn: () => request('get', '/overview'),
    refetchInterval: 60000,
  });
  const { data: alertsData } = useQuery({
    queryKey: ['admin-home-alerts'],
    queryFn: () => request('get', '/alerts'),
    refetchInterval: 60000,
  });
  const { data: activityData } = useQuery({
    queryKey: ['admin-home-activity'],
    queryFn: () => request('get', '/activity?limit=20'),
    refetchInterval: 60000,
  });
  const { data: healthData } = useQuery({
    queryKey: ['admin-home-health'],
    queryFn: () => request('get', '/module-health'),
    refetchInterval: 120000,
  });

  const isSuper = overview?.scope?.is_super;

  // Real-time WebSocket — live alerts feed
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const tenantId = user?.tenant_id || '';
    const wsBase = (API_URL || '').replace(/^http/, 'ws');
    const url = `${wsBase}/api/ws/admin-alerts?token=${encodeURIComponent(token)}${tenantId ? `&tenant_id=${encodeURIComponent(tenantId)}` : ''}`;
    let cancelled = false;
    let pingTimer;

    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => { if (!cancelled) setLiveStatus('live'); };
        ws.onclose = () => { if (!cancelled) { setLiveStatus('offline'); setTimeout(connect, 4000); } };
        ws.onerror = () => { if (!cancelled) setLiveStatus('offline'); };
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === 'admin_alert') {
              setLiveFeed((prev) => [{ ...msg, _at: Date.now() }, ...prev].slice(0, 8));
              // Toast on severity >= medium
              const sev = (msg.severity || 'medium').toLowerCase();
              if (sev === 'critical' || sev === 'urgent' || sev === 'high') {
                toast.error(msg.title || 'New alert', { description: msg.message, duration: 8000 });
              } else if (sev === 'medium' || sev === 'warning') {
                toast.warning(msg.title || 'New alert', { description: msg.message, duration: 6000 });
              } else {
                toast(msg.title || 'Update', { description: msg.message });
              }
              // Invalidate so KPIs/alerts/activity refresh
              queryClient.invalidateQueries({ queryKey: ['admin-home-overview'] });
              queryClient.invalidateQueries({ queryKey: ['admin-home-alerts'] });
              queryClient.invalidateQueries({ queryKey: ['admin-home-activity'] });
            }
          } catch (_) { /* swallow */ }
        };
        pingTimer = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25000);
      } catch (_) {
        setLiveStatus('offline');
        setTimeout(connect, 4000);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current?.close(); } catch (_) { /* noop */ }
    };
    // Only reconnect when tenant changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenant_id]);

  const kpis = overview?.kpis || {};
  const alerts = alertsData?.alerts || [];
  const activity = activityData?.activity || [];
  const modules = healthData?.modules || [];
  const hSum = healthData?.summary || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-900 text-white" data-testid="admin-home-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center">
            <Home className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight">Command Center</h1>
            <div className="text-xs text-slate-400 flex items-center gap-1">
              Welcome back, {user?.name || user?.email || 'admin'} ·
              <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-300 border-indigo-400/30 capitalize">
                {user?.role || 'admin'}
              </Badge>
            </div>
          </div>
          <GlobalSearchBox />
          <LiveStatusBadge status={liveStatus} />
          <Button size="sm" variant="outline" onClick={() => navigate('/admin')}
                  className="text-white border-white/20 bg-white/5 hover:bg-white/10" data-testid="go-to-admin-btn">
            <LayoutDashboard className="h-4 w-4 mr-1" /> Classic Admin
          </Button>
          {isSuper && (
            <Button size="sm" onClick={() => navigate('/superadmin')}
                    className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="go-to-superadmin-btn">
              <Shield className="h-4 w-4 mr-1" /> Super Admin
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Live feed (real-time WebSocket alerts) */}
        {liveFeed.length > 0 && (
          <Card className="bg-gradient-to-br from-indigo-900/30 via-slate-900/50 to-slate-950/60 border-indigo-400/20 text-white" data-testid="admin-home-live-feed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Radio className="h-4 w-4 text-emerald-400 animate-pulse" />
                Live Feed
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-400/30">
                  {liveFeed.length} real-time
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 pb-4">
              {liveFeed.map((e, i) => (
                <LiveFeedRow key={`${e._at}-${i}`} event={e} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Alerts bar */}
        {alerts.length > 0 && (
          <div className="space-y-2" data-testid="admin-home-alerts">
            {alerts.slice(0, 6).map((a, i) => (
              <AlertRow key={i} alert={a} index={i} />
            ))}
          </div>
        )}

        {/* KPI Grid */}
        <section data-testid="admin-home-kpis">
          <h2 className="text-sm uppercase tracking-wider text-slate-500 mb-3">At-a-glance</h2>
          {isSuper ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KPI icon={Building2} label="Active Tenants" value={kpis.tenants_active ?? 0} sub={`${kpis.tenants_total ?? 0} total · ${kpis.tenants_trial ?? 0} trial`} accent="indigo" />
              <KPI icon={TrendingUp} label="Active Subs" value={kpis.active_subscriptions ?? 0} sub={`${kpis.trials_ending_7d ?? 0} trials ending`} accent="emerald" />
              <KPI icon={DollarSign} label="Revenue (30d)" value={`$${kpis.revenue_30d ?? 0}`} sub={`${kpis.unpaid_invoices ?? 0} unpaid`} accent="emerald" />
              <KPI icon={Inbox} label="Applications" value={kpis.applications_pending ?? 0} sub="awaiting review" accent="fuchsia" />
              <KPI icon={LifeBuoy} label="Support Open" value={kpis.support_open ?? 0} sub={`${kpis.support_urgent ?? 0} urgent`} accent="sky" />
              <KPI icon={ShieldAlert} label="Safeguarding Open" value={kpis.safeguarding_open ?? 0} sub={`${kpis.safeguarding_flagged ?? 0} flagged`} accent="rose" />
              <KPI icon={FileWarning} label="SG Cases Open" value={kpis.safeguarding_cases_open ?? 0} sub="across all tenants" accent="orange" />
              <KPI icon={AlertTriangle} label="Critical Behaviour (30d)" value={kpis.behaviour_critical_30d ?? 0} accent="amber" />
              {/* iter-378 — Churches KPI surfaces the org hierarchy on the
                  Command Center. Clicking opens the Hierarchy Explorer where
                  admins can move/re-level churches. Orphan callout appears
                  only when there are churches NOT attached to the tree. */}
              <KPI
                icon={Church}
                label="Churches"
                value={kpis.churches_active ?? 0}
                sub={
                  (kpis.churches_orphans ?? 0) > 0
                    ? `${kpis.churches_orphans} unassigned · click to fix`
                    : `${kpis.churches_total ?? 0} total · ${(kpis.churches_by_level?.hq ?? 0)} HQ`
                }
                accent={(kpis.churches_orphans ?? 0) > 0 ? 'rose' : 'fuchsia'}
                to="/admin/churches/hierarchy"
                testId="kpi-churches"
              />
              {/* iter-383 — Password Reset Health card */}
              <KPI
                icon={ShieldAlert}
                label="Resets · 24h"
                value={kpis.resets_24h ?? 0}
                sub={
                  (kpis.reset_links_outstanding ?? 0) > 0
                    ? `${kpis.reset_links_outstanding} reset links unused`
                    : 'no pending links'
                }
                accent={(kpis.reset_links_outstanding ?? 0) > 5 ? 'amber' : 'sky'}
                testId="kpi-reset-health"
              />
              {/* iter-383 — CRM Contacts card */}
              {(kpis.crm_contacts_total ?? 0) > 0 && (
                <KPI
                  icon={Users}
                  label="CRM Contacts"
                  value={kpis.crm_contacts_total ?? 0}
                  sub={`${kpis.crm_contacts_dnc ?? 0} on do-not-contact`}
                  accent="indigo"
                  to="/admin/crm"
                  testId="kpi-crm"
                />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KPI icon={Users} label="Students" value={kpis.students_total ?? 0} accent="indigo" />
              <KPI icon={Users} label="Staff" value={kpis.staff_total ?? 0} accent="emerald" />
              <KPI icon={Building2} label="Classes" value={kpis.classes_total ?? 0} accent="sky" />
              <KPI icon={AlertTriangle} label="Behaviour Open" value={kpis.behaviour_open ?? 0} accent="amber" />
              <KPI icon={ShieldAlert} label="Safeguarding Open" value={kpis.safeguarding_open ?? 0} accent="rose" />
              <KPI icon={Inbox} label="Admissions Pending" value={kpis.admissions_pending ?? 0} accent="fuchsia" />
              <KPI icon={LifeBuoy} label="Support Open" value={kpis.support_tickets_open ?? 0} accent="sky" />
            </div>
          )}
        </section>

        <section className="grid lg:grid-cols-3 gap-5">
          {/* iter-369 — Copilot Ecosystem Health widget */}
          {user?.tenant_id && (
            <div className="lg:col-span-3">
              <CopilotBriefingWidget tenantId={user.tenant_id} />
            </div>
          )}

          {/* Quick Actions */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid="admin-home-quick-actions">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-fuchsia-400" /> Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {isSuper ? (
                <>
                  <QuickAction icon={Building2} label="Tenants" to="/superadmin" />
                  <QuickAction icon={Church} label="Church Hierarchy" to="/admin/churches/hierarchy" badge={kpis.churches_orphans} />
                  <QuickAction icon={QrCode} label="QR Health" to="/admin/qr-health" />
                  <QuickAction icon={Users} label="CRM" to="/admin/crm" />
                  <QuickAction icon={Users} label="People" to="/admin/people" />
                  <QuickAction icon={Building2} label="Accommodation ops" to="/admin/accommodation-ops" />
                  <QuickAction icon={Shield} label="Campus security" to="/admin/security/operations" />
                  <QuickAction icon={Database} label="Backup Centre" to="/admin/backup" />
                  <QuickAction icon={QrCode} label="Badge Designer" to="/admin/badge-designer" />
                  <QuickAction icon={QrCode} label="QR Scanner" to="/admin/qr-scanner" />
                  <QuickAction icon={QrCode} label="QR Labels" to="/admin/qr-labels" />
                  <QuickAction icon={Video} label="OBS Broadcast" to="/admin/obs" />
                  <QuickAction icon={Film} label="Video Editor" to="/admin/video-editor" />
                  <QuickAction icon={Wifi} label="Wi-Fi & Vouchers" to="/admin/wifi" />
                  <QuickAction icon={Inbox} label="Review Apps" to="/superadmin" badge={kpis.applications_pending} />
                  <QuickAction icon={CreditCard} label="Billing" to="/superadmin" />
                  <QuickAction icon={Megaphone} label="Broadcasts" to="/superadmin" />
                  <QuickAction icon={ShieldAlert} label="Compliance" to="/superadmin" />
                  <QuickAction icon={TrendingUp} label="Analytics" to="/superadmin" />
                  <QuickAction icon={LifeBuoy} label="Support" to="/superadmin" badge={kpis.support_open} />
                  <QuickAction icon={ScrollText} label="Audit" to="/superadmin" />
                </>
              ) : (
                <>
                  <QuickAction icon={LayoutDashboard} label="My Schools" to="/admin/schools" />
                  <QuickAction icon={LifeBuoy} label="Platform Support" to="/admin" />
                  <QuickAction icon={Megaphone} label="Announcements" to="/admin" />
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white lg:col-span-2" data-testid="admin-home-activity">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-400" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {activity.length === 0 && <p className="text-sm text-slate-500">No recent activity.</p>}
                {activity.map((a, i) => {
                  const Icon = ICON_MAP[a.icon] || Activity;
                  return (
                    <div key={i} className="flex items-center gap-3 text-sm py-1.5 px-2 rounded hover:bg-white/5">
                      <Icon className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                      <span className="flex-1 truncate">{a.summary}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {a.at ? new Date(a.at).toLocaleString() : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Module Health */}
        <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid="admin-home-module-health">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-indigo-400" /> Module Health</span>
              <div className="flex gap-2 text-xs">
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/30">Healthy: {hSum.healthy ?? 0}</Badge>
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30">Empty: {hSum.empty ?? 0}</Badge>
                <Badge className="bg-slate-500/20 text-slate-300 border-slate-400/30">Not installed: {hSum.not_installed ?? 0}</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {modules.map(m => {
                const cfg = {
                  healthy: { icon: CheckCircle2, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-400/20' },
                  empty: { icon: Circle, color: 'text-amber-400 bg-amber-500/10 border-amber-400/20' },
                  not_installed: { icon: XCircle, color: 'text-slate-500 bg-slate-500/10 border-slate-400/20' },
                }[m.status] || { icon: Circle, color: 'text-slate-500 bg-slate-500/10 border-slate-400/20' };
                const Icon = cfg.icon;
                return (
                  <div key={m.id} className={`flex items-center gap-2 p-2 rounded border ${cfg.color}`}
                       data-testid={`module-health-${m.id}`}>
                    <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{m.label}</p>
                      <p className="text-[10px] text-slate-400">{m.doc_count} records</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

const LiveStatusBadge = ({ status }) => {
  if (status === 'live') {
    return (
      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-400/30 flex items-center gap-1" data-testid="live-status-badge">
        <Radio className="h-3 w-3 animate-pulse" /> Live
      </Badge>
    );
  }
  if (status === 'connecting') {
    return (
      <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-400/30 flex items-center gap-1" data-testid="live-status-badge">
        <Radio className="h-3 w-3 animate-pulse" /> Connecting
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] bg-slate-500/10 text-slate-300 border-slate-400/30 flex items-center gap-1" data-testid="live-status-badge">
      <WifiOff className="h-3 w-3" /> Offline
    </Badge>
  );
};

const LiveFeedRow = ({ event }) => {
  const sev = (event.severity || 'info').toLowerCase();
  const sevStyle = {
    critical: 'text-rose-300',
    urgent: 'text-rose-300',
    high: 'text-orange-300',
    medium: 'text-amber-300',
    warning: 'text-amber-300',
    info: 'text-sky-300',
    low: 'text-slate-300',
  }[sev] || 'text-slate-300';
  const when = new Date(event._at || event.timestamp || Date.now()).toLocaleTimeString();
  return (
    <Link
      to={event.link || '/admin/home'}
      className="flex items-center gap-3 px-2.5 py-1.5 rounded-md bg-slate-950/50 hover:bg-slate-900/70 border border-white/5 transition-colors"
      data-testid={`live-feed-row-${event.event_type}`}
    >
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${sevStyle}`}>{sev}</span>
      <span className="text-xs font-medium text-slate-100 flex-1 truncate">{event.title}</span>
      <span className="text-[10px] text-slate-500 hidden md:inline truncate max-w-xs">{event.message}</span>
      <span className="text-[10px] text-slate-500">{when}</span>
    </Link>
  );
};

const KPI = ({ icon: Icon, label, value, sub, accent, to, testId }) => {
  const iconBg = {
    indigo: 'bg-indigo-500/20 border-indigo-400/30 text-indigo-300',
    emerald: 'bg-emerald-500/20 border-emerald-400/30 text-emerald-300',
    sky: 'bg-sky-500/20 border-sky-400/30 text-sky-300',
    fuchsia: 'bg-fuchsia-500/20 border-fuchsia-400/30 text-fuchsia-300',
    rose: 'bg-rose-500/20 border-rose-400/30 text-rose-300',
    orange: 'bg-orange-500/20 border-orange-400/30 text-orange-300',
    amber: 'bg-amber-500/20 border-amber-400/30 text-amber-300',
  }[accent] || 'bg-slate-500/20 border-slate-400/30 text-slate-300';
  const body = (
    <Card className={`bg-white/5 backdrop-blur-xl border-white/10 text-white ${to ? 'hover:bg-white/10 transition-colors cursor-pointer' : ''}`}
          data-testid={testId || `kpi-home-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-4 pb-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${iconBg} flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const QuickAction = ({ icon: Icon, label, to, badge }) => (
  <Link to={to}
        className="flex items-center gap-2 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
        data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <Icon className="h-4 w-4 text-slate-300" />
    <span className="text-xs font-semibold flex-1 truncate">{label}</span>
    {badge > 0 && <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/30 text-[10px] px-1.5 py-0">{badge}</Badge>}
  </Link>
);

const GlobalSearchBox = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (query) => {
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const d = await request('get', `/search?q=${encodeURIComponent(query)}`);
      setResults(d.results || []);
    } catch (_) { setResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (q) search(q); }, 250);
    return () => clearTimeout(t);
  }, [q, search]);

  return (
    <div className="relative min-w-0 sm:min-w-[260px] max-w-sm">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
      <Input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search tenants, tickets, students..."
        className="pl-9 bg-white/5 border-white/10 text-white text-sm"
        data-testid="global-search-input"
      />
      {open && q.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-white/10 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto"
             data-testid="global-search-results">
          {loading && <p className="p-3 text-xs text-slate-500">Searching...</p>}
          {!loading && results.length === 0 && <p className="p-3 text-xs text-slate-500">No results.</p>}
          {results.map((r, i) => (
            <Link key={i} to={r.link} onClick={() => { setQ(''); setOpen(false); }}
                  className="flex items-center gap-2 p-2 hover:bg-white/5 border-b border-white/5">
              <Badge variant="outline" className="text-[9px] capitalize bg-white/5 border-white/10">{r.type}</Badge>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{r.title}</p>
                <p className="text-[10px] text-slate-500 truncate">{r.subtitle}</p>
              </div>
              <ExternalLink className="h-3 w-3 text-slate-500" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertRow = ({ alert, index }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = ICON_MAP[alert.icon] || AlertTriangle;
  // Map alert link_tab → inline resource type so we can fetch a quick preview
  const resourceType = {
    applications: 'applications',
    support: 'tickets',
    billing: 'invoices',
    tenants: 'tenants',
    compliance: 'compliance',
  }[alert.link_tab];

  return (
    <div className={`rounded-lg border ${SEVERITY_STYLES[alert.severity]}`}
         data-testid={`alert-${alert.severity}-${index}`}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium flex-1">{alert.title}</span>
        {resourceType && (
          <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}
                  className="h-7 px-2 text-current hover:bg-white/10"
                  data-testid={`alert-expand-${index}`}>
            {expanded ? 'Hide' : 'Triage'} {expanded ? '↑' : '↓'}
          </Button>
        )}
        <Link to={alert.link + (alert.link_tab ? `?tab=${alert.link_tab}` : '')}
              className="opacity-70 hover:opacity-100">
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {expanded && resourceType && (
        <div className="border-t border-white/10 px-4 py-3 bg-slate-950/50 rounded-b-lg">
          <AlertInlineDrawer type={resourceType} />
        </div>
      )}
    </div>
  );
};

const AlertInlineDrawer = ({ type }) => {
  const token = () => getToken();
  const endpoint = {
    applications: '/api/superadmin/applications?status=submitted',
    tickets: '/api/superadmin/tickets?status=open',
    invoices: '/api/superadmin/invoices?status=overdue',
    tenants: '/api/superadmin/tenants?status=trial',
    compliance: '/api/superadmin/compliance/overview?days=30',
  }[type];

  const { data, isLoading } = useQuery({
    queryKey: ['alert-inline', type],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token()}` } });
      return res.data;
    },
  });

  if (isLoading) return <p className="text-xs text-slate-400">Loading…</p>;

  if (type === 'compliance') {
    return (
      <div className="flex gap-3 flex-wrap text-xs">
        <span>🔴 High-risk cases: <b>{data?.high_risk_cases_count ?? 0}</b></span>
        <span>⏰ Overdue reviews: <b>{data?.overdue_reviews_count ?? 0}</b></span>
        <span>🚨 Urgent open: <b>{data?.urgent_open ?? 0}</b></span>
        <Link to="/superadmin?tab=compliance" className="underline font-semibold ml-auto">Open Compliance →</Link>
      </div>
    );
  }

  const items = Array.isArray(data) ? data.slice(0, 5) : [];
  if (items.length === 0) return <p className="text-xs text-slate-400">Nothing to show.</p>;

  return (
    <div className="space-y-1 text-xs">
      {items.map((it, i) => {
        if (type === 'applications') {
          return (
            <div key={i} className="flex items-center gap-2 py-1 border-b border-white/5">
              <Inbox className="h-3 w-3 text-fuchsia-400" />
              <span className="font-semibold">{it.school_name}</span>
              <span className="text-slate-400">{it.contact_email}</span>
              <Link to="/superadmin" className="ml-auto underline">Review</Link>
            </div>
          );
        }
        if (type === 'tickets') {
          return (
            <div key={i} className="flex items-center gap-2 py-1 border-b border-white/5">
              <LifeBuoy className="h-3 w-3 text-sky-400" />
              <span className="font-mono">{it.number}</span>
              <span className="font-semibold truncate">{it.subject}</span>
              <Badge className="text-[9px] bg-white/10">{it.priority}</Badge>
              <Link to="/superadmin" className="ml-auto underline">Open</Link>
            </div>
          );
        }
        if (type === 'invoices') {
          return (
            <div key={i} className="flex items-center gap-2 py-1 border-b border-white/5">
              <CreditCard className="h-3 w-3 text-amber-400" />
              <span className="font-mono">{it.number}</span>
              <span>${it.amount}</span>
              <span className="text-slate-400">{it.tenant_name}</span>
              <Link to="/superadmin" className="ml-auto underline">Open</Link>
            </div>
          );
        }
        if (type === 'tenants') {
          return (
            <div key={i} className="flex items-center gap-2 py-1 border-b border-white/5">
              <Building2 className="h-3 w-3 text-indigo-400" />
              <span className="font-semibold">{it.name}</span>
              <span className="text-slate-400">{it.code}</span>
              <Badge className="text-[9px] bg-amber-500/20 text-amber-200">{it.status}</Badge>
              <Link to={`/admin/schools/${it.tenant_id || it.id}`} className="ml-auto underline">Open</Link>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

export default AdminHome;
